import { Seo } from "../components/Seo";
import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, Download, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { PageHero } from "../components/PageHero";
import { categories, allProducts, totalProductCount } from "../data/products";
import { VG_ORANGE, CATALOG_PDF } from "../lib/brand";
import { renderProductName } from "../lib/productName";
import { HolidayCollectionCard } from "../components/HolidayPromo";

const FilterPanel = ({ selected, toggle, clearAll }) => (
  <div data-testid="products-filters">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4" style={{ color: VG_ORANGE }} />
        <span className="font-heading font-bold text-neutral-900">Filters</span>
      </div>
      {selected.length > 0 && (
        <button onClick={clearAll} data-testid="clear-filters" className="text-xs font-medium text-neutral-500 hover:text-[#ED8B00]">
          Clear All
        </button>
      )}
    </div>

    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3">Category</h3>
      <div className="space-y-2.5">
        {categories.map((cat) => (
          <label
            key={cat.slug}
            data-testid={`filter-${cat.slug}`}
            className="flex items-center gap-2.5 text-sm text-neutral-700 cursor-pointer hover:text-neutral-900"
          >
            <Checkbox
              checked={selected.includes(cat.slug)}
              onCheckedChange={() => toggle(cat.slug)}
            />
            <span className="flex-1">{cat.name}</span>
            <span className="text-xs text-neutral-400">({cat.products.length})</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);

export default function Products() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "";
  const parseCategories = (raw) =>
    raw.split(",").map((s) => s.trim()).filter((slug) => categories.some((c) => c.slug === slug));
  const [selected, setSelected] = useState(() => parseCategories(categoryParam));
  const [q, setQ] = useState("");

  // Re-sync filters when the ?category= param changes while already on this page
  // (e.g. picking a different category from the nav dropdown).
  useEffect(() => {
    setSelected(parseCategories(categoryParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryParam]);

  const toggle = (slug) =>
    setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  const clearAll = () => setSelected([]);

  const filtered = useMemo(() => {
    return allProducts.filter((p) => {
      const catOk = selected.length === 0 || selected.includes(p.categorySlug);
      const qOk = p.name.toLowerCase().includes(q.toLowerCase()) || p.categoryName.toLowerCase().includes(q.toLowerCase());
      return catOk && qOk;
    });
  }, [selected, q]);

  return (
    <div data-testid="products-page">
      <Seo title="Products" description="Browse van Gelder's full commercial flooring catalog — broadloom, carpet tile, custom logo mats, luxury vinyl, entry grid systems, commercial mats, anti-fatigue matting, and recycled rubber flooring." />
      <PageHero
        title="Products"
        subtitle={`Explore our complete catalog of ${totalProductCount} commercial flooring solutions. From broadloom to luxury vinyl, find the perfect floor for your space.`}
        testId="products-hero"
      />

      {/* Download full catalog */}
      <section className="mx-auto max-w-7xl px-4 pt-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-neutral-200 bg-neutral-50 p-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-neutral-900">Browse the full catalog</h2>
            <p className="text-sm text-neutral-600">View our complete product range in the van Gelder, Inc. digital catalog — or download a copy.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button asChild className="text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }} data-testid="catalog-view-btn">
              <Link to="/catalog">
                <Eye className="mr-2 h-4 w-4" /> View Catalog
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-[#ED8B00] text-[#ED8B00] hover:bg-orange-50" data-testid="catalog-download-btn">
              <a href={CATALOG_PDF} download>
                <Download className="mr-2 h-4 w-4" /> Download PDF
              </a>
            </Button>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 grid gap-8 lg:grid-cols-[260px_1fr]">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-xl border border-neutral-200 bg-white p-5">
            <FilterPanel selected={selected} toggle={toggle} clearAll={clearAll} />
          </div>
        </aside>

        {/* Main */}
        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div className="flex items-center gap-3">
              {/* Mobile filter trigger */}
              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <button data-testid="mobile-filter-trigger" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium">
                      <SlidersHorizontal className="h-4 w-4" /> Filters
                      {selected.length > 0 && (
                        <span className="ml-1 rounded-full bg-[#ED8B00] px-1.5 text-xs text-white">{selected.length}</span>
                      )}
                    </button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 overflow-y-auto">
                    <div className="mt-6">
                      <FilterPanel selected={selected} toggle={toggle} clearAll={clearAll} />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
              <p className="text-sm font-medium text-neutral-600" data-testid="products-count">
                {filtered.length} Product{filtered.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input
                data-testid="products-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products..."
                className="pl-9"
              />
            </div>
          </div>

          {/* Active filter chips */}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {selected.map((slug) => {
                const cat = categories.find((c) => c.slug === slug);
                return (
                  <button
                    key={slug}
                    onClick={() => toggle(slug)}
                    className="flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-[#ED8B00]"
                  >
                    {cat?.name} <X className="h-3 w-3" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Holiday Mat Program collection card (shown with Custom Logos) */}
          {(selected.length === 0 || selected.includes("custom-logos")) && <HolidayCollectionCard />}

          {/* Grid */}
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((p) => (
              <Link
                key={`${p.categorySlug}-${p.slug}`}
                to={`/products/${p.categorySlug}/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                <div className="h-44 overflow-hidden" style={{ backgroundColor: p.isPlaceholder ? "#EC008C" : (p.thumbFit === "contain" || p.categorySlug === "accessories") ? "#ffffff" : "#f5f5f5" }}>
                  {p.isPlaceholder ? (
                    <img src="/products/placeholder.jpg" alt={p.name} className="h-full w-full object-contain" />
                  ) : (p.thumbFit === "contain" || p.categorySlug === "accessories") ? (
                    <img src={p.image} alt={p.name} className={`h-full w-full object-contain transition-transform duration-500 group-hover:scale-105 ${p.categorySlug === "accessories" ? "p-2" : "p-4"}`} />
                  ) : (
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                    {p.categoryName}
                  </span>
                  <h3 className="mt-1 font-heading text-base font-bold text-neutral-900 group-hover:text-[#ED8B00]">
                    {renderProductName(p.name)}
                  </h3>
                  <span className="mt-auto pt-3 text-xs text-neutral-500">Multiple colors • View details</span>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-20 text-center text-neutral-500" data-testid="no-results">
              No products match your filters.
              <button onClick={() => { clearAll(); setQ(""); }} className="ml-1 font-semibold text-[#ED8B00] hover:underline">
                Reset
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
